<template>
  <div
    v-if="attrs.impersonating"
    class="flex h-12 w-full items-center justify-center bg-indigo-500 font-bold text-gray-100">
    Impersonating {{ $page.props.user.name }} (<Link
      :href="route('impersonate.leave')"
      class="hover:text-white">
      Leave
    </Link>)
  </div>
  <div
    id="app"
    class="flex h-screen overflow-hidden bg-gray-100">
    <!-- Off-canvas menu for mobile, show/hide based on off-canvas menu state. -->
    <div
      v-if="isHidden"
      class="main-menu md:hidden">
      <div class="fixed inset-0 z-40 flex">
        <!--
                    Off-canvas menu overlay, show/hide based on off-canvas menu state.

                    Entering: "transition-opacity ease-linear duration-300"
                        From: "opacity-0"
                        To: "opacity-100"
                    Leaving: "transition-opacity ease-linear duration-300"
                        From: "opacity-100"
                        To: "opacity-0"
                -->
        <div
          class="fixed inset-0"
          aria-hidden="true">
          <div class="absolute inset-0 bg-gray-600 opacity-75" />
        </div>
        <!--
                    Off-canvas menu, show/hide based on off-canvas menu state.

                    Entering: "transition ease-in-out duration-300 transform"
                        From: "-translate-x-full"
                        To: "translate-x-0"
                    Leaving: "transition ease-in-out duration-300 transform"
                        From: "translate-x-0"
                        To: "-translate-x-full"
                -->
        <div
          class="relative flex w-full max-w-xs flex-1 flex-col bg-white pb-4 pt-5">
          <div class="absolute right-0 top-0 -mr-12 pt-2">
            <button
              class="ml-1 flex size-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              @click="isHidden = !isHidden">
              <span class="sr-only">Close sidebar</span>
              <!-- Heroicon name: x -->
              <svg
                class="size-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="flex shrink-0 items-center px-4">
            <img
              class="h-5 w-auto"
              src="/img/traveloffice-logo.svg"
              alt="TravelOffice">
          </div>

          <!-- Mobile Navigation Menu -->
          <div class="mt-5 h-0 flex-1 overflow-y-auto">
            <nav class="px-2">
              <div
                v-for="(group, indexG) in filteredNavigationItems"
                :key="'nav-group-'+ indexG"
                class="my-4">
                <!-- Group links -->
                <!-- Current class list: "bg-gray-100 text-gray-900", Default: "text-gray-600 hover:bg-gray-100 hover:text-gray-900" -->
                <Link
                  v-for="(item, indexI) in group"
                  :key="'nav-group-item-'+ indexI"
                  :href="route(item.route)"
                  class="group flex items-center rounded-md p-2 text-base font-medium leading-5 text-gray-600 transition duration-150 ease-in-out
                  hover:bg-gray-100 hover:text-gray-900 hover:underline focus:bg-gray-100 focus:text-gray-900 focus:outline-none"
                  :class="item.class"
                  exact
                  @click="isHidden = !isHidden">
                  <component
                    :is="item.icon"
                    class="mr-4 size-6 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true" />
                  {{ item.name }}
                </Link>
                <!-- Group separator -->
                <div
                  v-if="indexG !== filteredNavigationItems.length - 1 && group.length > 0"
                  class="relative my-4 py-1">
                  <div
                    class="absolute inset-0 flex items-center"
                    aria-hidden="true">
                    <div
                      class="w-full border-t border-gray-300" />
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>
        <div
          class="w-14 shrink-0"
          aria-hidden="true">
          <!-- Dummy element to force sidebar to shrink to fit close icon -->
        </div>
      </div>
    </div>

    <!-- Static sidebar for desktop -->
    <div class="main-menu hidden md:flex md:shrink-0">
      <div class="flex w-60 flex-col">
        <!-- Sidebar component, swap this element with another sidebar if you like -->
        <div class="flex grow flex-col overflow-y-auto border-r border-gray-200 bg-white pb-4 pt-5">
          <!-- Logo -->
          <div class="flex shrink-0 items-center px-4">
            <img
              class="h-5 w-auto"
              src="/img/traveloffice-logo.svg"
              alt="TravelOffice">
          </div>

          <!-- Desktop Navigation Menu -->
          <div class="mt-5 flex grow flex-col">
            <nav class="flex-1 space-y-1 bg-white px-2">
              <div
                v-for="(group, indexG) in filteredNavigationItems"
                :key="'nav-group-'+ indexG"
                class="my-4">
                <!-- Group links -->
                <!-- Current: "bg-gray-100 text-gray-900", Default: "text-gray-600 hover:bg-gray-50 hover:text-gray-900" -->
                <nav-link
                  v-for="(item, indexI) in group"
                  :key="'nav-group-item-'+ indexI"
                  :href="route(item.route)"
                  :active="route().current(item.route)"
                  :class="item.class">
                  <component
                    :is="item.icon"
                    class="mr-4 size-6 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true" />
                  {{ item.name }}
                </nav-link>
                <!-- Group separator -->
                <div
                  v-if="indexG !== filteredNavigationItems.length - 1 && group.length > 0"
                  class="relative my-4 py-1">
                  <div
                    class="absolute inset-0 flex items-center"
                    aria-hidden="true">
                    <div
                      class="w-full border-t border-gray-300" />
                  </div>
                </div>
              </div>
            </nav>
            <p class="text-center text-xs text-gray-300">
              Version {{ $page.props.version }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Dynamic content frame -->
    <div
      id="main-content"
      class="flex w-0 flex-1 flex-col overflow-hidden">
      <!-- Header content frame -->
      <div class="z-5 relative flex h-16 shrink-0 bg-white shadow">
        <!-- Sidebar toggle, mobile -->
        <button
          class="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
          @click="isHidden = !isHidden">
          <span class="sr-only">Open sidebar</span>
          <Bars3BottomLeftIcon
            class="size-6"
            aria-hidden="true" />
        </button>

        <!-- Header content -->
        <div class="flex flex-1 justify-between border-b border-b-gray-200 px-4">
          <!-- Top bar -->
          <div class="flex flex-1 items-center space-x-4">
            <!-- Team information -->
            <div
              class="flex cursor-pointer flex-row items-center space-x-2
                rounded-full border border-gray-200 bg-gray-50 p-2"
              title="Click to switch teams"
              @click.stop="showProfileMenu=!showProfileMenu">
              <img
                class="h-6 w-auto rounded-full object-cover "
                :src="$page.props.user.current_team.photo_url"
                :alt="$page.props.user.current_team.name">
              <p
                class="pr-2 text-sm font-semibold">
                {{ $page.props.user.current_team.personal_team ? 'Personal projects' : $page.props.user.current_team.name }}
              </p>
            </div>
            <!-- Search bar -->
            <!-- //TODO: enable when search is available
            <div class="flex flex-1 border border-gray-200 rounded-full p-1 px-2">
              <form
                class="flex w-full md:ml-0"
                action="#"
                method="GET">
                <label
                  for="search_field"
                  class="sr-only">Search</label>
                <div
                  class="relative w-full text-gray-400 focus-within:text-gray-600">
                  <div
                    class="pointer-events-none absolute inset-y-0 left-0 flex items-center">
                    &lt;!&ndash; Heroicon name: search &ndash;&gt;
                    <svg
                      class="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true">
                      <path
                        fill-rule="evenodd"
                        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                        clip-rule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="search_field"
                    class="block h-full w-full cursor-not-allowed border-transparent py-2 pl-8 pr-3 text-gray-900
                    placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-0 focus:placeholder:text-gray-400
                    sm:text-sm lg:w-1/2"
                    placeholder="Search"
                    type="search"
                    name="search">
                </div>
              </form>
            </div>
            -->
          </div>

          <!-- User information -->
          <div class="ml-4 flex items-center space-x-2 md:ml-6">
            <!-- Notification dropdown -->
            <button
              class="cursor-not-allowed rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
              <span class="sr-only">View notifications</span>
              <BellIcon
                class="size-6"
                aria-hidden="true" />
            </button>
            <!-- Profile dropdown -->
            <ProfileMenu
              :show="showProfileMenu"
              @update:show="showProfileMenu = $event" />
          </div>
        </div>
      </div>

      <!-- Main content -->
      <main
        class="relative flex-1 overflow-y-auto focus:outline-none"
        tabindex="0">
        <alerts />
        <div :class="{'p-4': !$page.url.includes('/proposals/') }">
          <slot />
        </div>
      </main>
    </div>
  </div>

  <NotificationGroup>
    <!-- https://github.com/emmanuelsw/notiwind -->
    <div
      aria-live="assertive"
      class="fixed right-0 top-10 z-[1000] flex items-start justify-end p-6 px-4">
      <div class="w-full max-w-sm">
        <Notification
          v-slot="{ notifications, close }"
          enter="transform ease-out duration-300 transition"
          enter-from="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4"
          enter-to="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-500"
          leave-from="opacity-100"
          leave-to="opacity-0"
          move="transition duration-500"
          move-delay="delay-300">
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="mx-auto mt-4 flex w-full max-w-sm justify-between overflow-hidden rounded-lg bg-white shadow-md">
            <div class="flex grow">
              <div
                class="flex items-start justify-center p-4"
                :class="{
                  'text-red-500':
                    notification.type === 'danger',
                  'text-yellow-500':
                    notification.type === 'warning',
                  'text-blue-400':
                    notification.type === 'info',
                  'text-green-500':
                    notification.type === 'success',
                }">
                <!-- Success -->
                <svg
                  v-if="notification.type === 'success'"
                  xmlns="http://www.w3.org/2000/svg"
                  class="size-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <!-- Warning -->
                <svg
                  v-else-if="notification.type === 'warning'"
                  xmlns="http://www.w3.org/2000/svg"
                  class="size-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <!-- Info -->
                <svg
                  v-else-if="notification.type === 'info'"
                  xmlns="http://www.w3.org/2000/svg"
                  class="size-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <!-- Danger -->
                <svg
                  v-else-if="notification.type === 'danger'"
                  xmlns="http://www.w3.org/2000/svg"
                  class="size-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <!-- Undefined -->
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  class="size-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <div
                class="-mx-3 px-4 py-2"
                :class="{'py-4': !notification.text}">
                <div class="mx-3">
                  <p
                    class="text-sm font-medium text-gray-900">
                    {{ notification.title }}
                  </p>
                  <p
                    v-if="notification.text"
                    class="text-sm text-gray-600">
                    {{ notification.text }}
                  </p>
                </div>
              </div>
            </div>
            <button
              class="flex grow-0 items-start justify-center p-4 text-gray-400 hover:text-gray-900 focus:outline-none"
              @click="close(notification.id)">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="size-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </Notification>
      </div>
    </div>
  </NotificationGroup>
</template>

<script setup>
/* external */
import {
  Link,
} from '@inertiajs/vue3';
import {
  onMounted,
  ref, useAttrs,
} from 'vue';
import {
  BellIcon,
  HomeIcon,
  GlobeEuropeAfricaIcon,
  FolderOpenIcon,
  DocumentIcon,
  UserGroupIcon,
  InboxStackIcon,
  IdentificationIcon,
  ReceiptPercentIcon,
  DocumentPlusIcon,
  BuildingStorefrontIcon,
  BoltIcon,
} from '@heroicons/vue/24/outline';
import {
  Bars3BottomLeftIcon,
} from '@heroicons/vue/24/solid';
import {
  Notification, NotificationGroup,
} from 'notiwind';

/* local */
import Alerts from '@/Components/Alerts.vue';
import ProfileMenu from '@/Components/ProfileMenu.vue';
import NavLink from '../ComponentsDeprecated/NavLink.vue';

const attrs = useAttrs();
const showProfileMenu = ref(false);

const props = defineProps({
  team: {
    type: Object,
    required: true,
  },
});

const isHidden = ref(false);

const navigationItems = [
  [
    {
      name: 'Dashboard',
      route: 'dashboard',
      icon: HomeIcon,
      vIf: true,
      class: '',
    },
    {
      name: 'Destinations',
      route: 'destinations',
      icon: GlobeEuropeAfricaIcon,
      vIf: true,
      class: '',
    },
    {
      name: 'Clients',
      route: 'clients',
      icon: BuildingStorefrontIcon,
      vIf: true,
      class: '',
    },
    {
      name: 'Projects',
      route: 'projects',
      icon: FolderOpenIcon,
      vIf: true,
      class: '',
    },
    {
      name: 'Proposals',
      route: 'proposals',
      icon: DocumentIcon,
      vIf: true,
      class: '',
    },
  ],
  [
    {
      name: 'Guides',
      route: 'guides.index',
      icon: UserGroupIcon,
      vIf: !attrs.prod && attrs.features?.operating,
      class: '',
    },
    {
      name: 'My Engagements',
      route: 'engagements',
      icon: InboxStackIcon,
      vIf: !attrs.prod && (attrs.features?.operating || attrs.features?.guiding),
      class: '',
    },
    {
      name: 'My Guide Profile',
      route: 'user.profile.guide',
      icon: IdentificationIcon,
      vIf: !attrs.prod && attrs.features?.guiding,
      class: '',
    },
    {
      name: 'Invoice information',
      route: 'invoicing',
      icon: ReceiptPercentIcon,
      vIf: !attrs.prod && attrs.features?.invoicing,
      class: '',
    },
  ],
  [
    {
      name: 'Create new Proposal',
      route: 'proposals.create',
      icon: DocumentPlusIcon,
      vIf: true,
      class: '',
    },
  ],
  [
    {
      name: 'Admin tools',
      route: 'admin',
      icon: BoltIcon,
      vIf: attrs.roles.includes('admin'),
      class: '',
    },
  ],
];

const filteredNavigationItems = navigationItems.map((group) => group.filter((item) => item.vIf));

</script>
